/**
 * RUM events for the driver login code request, so the funnel can show what
 * happens between a tap on Continue / Resend and the request reaching
 * /api/embeddedLogin. Server logs only see requests that went out; these
 * cover the taps that didn't (SMS send limit, Turnstile hold, double taps).
 *
 * Pair with `turnstile_token_wait` (useTurnstileToken) for how long the
 * Turnstile check held the tap.
 */
import { datadogRum } from '@datadog/browser-rum'

import type { TurnstileMissReason } from 'src/components/Turnstile/useTurnstileToken'

type LoginStep = 'phone' | 'otp'

/** Why a send tap produced no request. */
export type SendSkipReason =
  /** A request or token wait was already running (double tap / Enter + tap). */
  | 'in-flight'
  /** 2-per-minute SMS limit; the driver sees "Try again in …". */
  | 'cooldown'
  /** SMS limit hit, but this number was just texted: jumped to the code step. */
  | 'cooldown-to-otp'
  /** First Turnstile timeout: widget shown, driver asked to tap again. */
  | 'needs-interaction'

export type SendOutcome = 'sent' | 'new-user' | 'error'

export const trackSendSkipped = (
  reason: SendSkipReason,
  step: LoginStep,
  prefilled: boolean
) => {
  datadogRum.addAction('driver_login_send_skipped', {
    reason,
    step,
    prefilled,
  })
}

export const trackCodeRequested = ({
  step,
  prefilled,
  hasToken,
  clientReason,
}: {
  step: LoginStep
  /** Sent from the number prefilled by the short-form handoff. */
  prefilled: boolean
  hasToken: boolean
  clientReason?: TurnstileMissReason
}) => {
  datadogRum.addAction('driver_login_code_requested', {
    step,
    prefilled,
    has_token: hasToken,
    client_reason: clientReason ?? null,
  })
}

export const trackCodeResult = ({
  outcome,
  step,
  prefilled,
  error,
  startedCooldown,
}: {
  outcome: SendOutcome
  step: LoginStep
  prefilled: boolean
  /** API error text (client-safe copy, never the phone number). */
  error?: string | null
  /** 'sent' only: this send hit the SMS limit and started the countdown. */
  startedCooldown?: boolean
}) => {
  datadogRum.addAction('driver_login_code_result', {
    outcome,
    step,
    prefilled,
    error: error ?? null,
    started_cooldown: startedCooldown ?? false,
  })
}
